import { useEffect, useRef, useCallback } from 'react';

const useAutoRefresh = (refreshFunction, intervalMinutes = 5, enabled = true, dependencies = []) => {
  const intervalRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());
  const isUserInteractingRef = useRef(false);

  // Track user interactions to pause refresh during active use
  const handleUserInteraction = useCallback(() => {
    isUserInteractingRef.current = true;
    setTimeout(() => {
      isUserInteractingRef.current = false;
    }, 10000);
  }, []);

  // Setup user interaction listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [handleUserInteraction]);

  useEffect(() => {
    if (!enabled || !refreshFunction) {
      return;
    }

    intervalRef.current = setInterval(() => {
      // Skip refresh if user is actively interacting
      if (isUserInteractingRef.current) {
        return;
      }

      // Skip refresh if page is not visible
      if (document.visibilityState !== 'visible') {
        return;
      }

      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshRef.current;
      const intervalMs = intervalMinutes * 60 * 1000;

      // Only refresh if enough time has passed
      if (timeSinceLastRefresh >= intervalMs) {
        lastRefreshRef.current = now;
        refreshFunction();
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshFunction, intervalMinutes, enabled, ...dependencies]);

  // Manual refresh function
  const manualRefresh = useCallback(() => {
    if (refreshFunction) {
      lastRefreshRef.current = Date.now();
      refreshFunction();
    }
  }, [refreshFunction]);

  // Function to sync the last refresh time (called from parent when data is actually refreshed)
  const syncLastRefreshTime = useCallback((refreshTime) => {
    lastRefreshRef.current = refreshTime.getTime();
  }, []);

  return { manualRefresh, syncLastRefreshTime };
};

export default useAutoRefresh;
